import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  Chip,
  IconButton,
  Paper,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  Search,
  Add,
} from "@mui/icons-material";
import { EditOutlined } from "@mui/icons-material";

import CreateCrop from "./CreateCrop";
import { getAllCropsAction, getByCropAction } from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";

export default function CropManagement() {
  const dispatch = useDispatch();
  const { successData} = useSelector((state) => state.getAllCropsReducer);
  const [openCropModal, setOpenCropModal] = useState(false);
  const [editingCropId, setEditingCropId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const crops = successData?.data?.crops || [];
  // Track if refresh is needed after modal closes
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const handleEdit = (crop) => {
    const id = crop?.cropId;
    if (!id) return;
    setEditingCropId(id);
    setOpenCropModal(true);
    dispatch(getByCropAction({ endPoint: `crop/${id}` }));
  };

  useEffect(() => {
    dispatch(getAllCropsAction());
  }, [dispatch]);

  // Refresh crops after modal closes if needed
  useEffect(() => {
    if (!openCropModal && needsRefresh) {
      dispatch(getAllCropsAction());
      setNeedsRefresh(false);
    }
  }, [openCropModal, needsRefresh, dispatch]);

  return (
    <Box
      sx={{
        minHeight: { xs: "100vh", md: "100vh" },
        p: { xs: 1, sm: 1, md: 0, lg: 0, xl: 0 },
        boxSizing: "border-box",
      }}
    >


      {/* Title */}
      <Typography
        mb={2}

        color="#111827"
        fontSize={18} fontWeight={600}
      >
        Crop Management
      </Typography>

      {/* Filters */}
      <Card
        sx={{
          p: { xs: 2, sm: 2, md: 2, lg: 2 },
          mb: { xs: 3, sm: 4 },
          borderRadius: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 2 },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >
        <TextField
          placeholder="Search ..."
          size="small"
          InputProps={{ startAdornment: <Search sx={{ mr: 1 }} /> }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 3 },
            width: { xs: "100%", sm: 200, md: 260 },
          }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <Box flexGrow={1} sx={{ display: { xs: "none", sm: "block" } }} />

        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{
            background: "#2563EB",
            textTransform: "none",
            borderRadius: 3,
            px: { xs: 2, sm: 3 },
            mt: { xs: 1, sm: 0 },
            width: { xs: "100%", sm: "auto" },
          }}
          onClick={() => {
            setEditingCropId(null); // ensure create mode
            setOpenCropModal(true);
          }}
        >
          Add New Crop
        </Button>

        {/* Modal for Add New Crop */}
        <Dialog
          open={openCropModal}
          onClose={() => {
            setOpenCropModal(false);
            setEditingCropId(null); // clear edit id on close
          }}
          maxWidth="sm"
          fullWidth
          aria-labelledby="add-crop-dialog"
        >
          <DialogContent sx={{ p: 0 }}>
            <CreateCrop
              onClose={() => {
                setOpenCropModal(false);
                setEditingCropId(null);
              }}
              cropId={editingCropId}
              onRefresh={() => setNeedsRefresh(true)}
            />
          </DialogContent>
        </Dialog>
      </Card>

      {/* Table Header: visible only on sm and up */}
      <Box
        display={{ xs: "none", sm: "grid" }}
        gridTemplateColumns={{ sm: "3fr 1fr 1fr" }}
        px={{ xs: 1, sm: 2 }}
        mb={{ xs: 1, sm: 1 }}
      >
        <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>
          CROP NAME
        </Typography>
        <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600}>
          STATUS
        </Typography>
        <Typography fontSize={{ sm: 12 }} color="#000000" fontWeight={600} sx={{ display: { sm: "block" } }}>
          ACTIONS
        </Typography>
      </Box>

      {/* Rows */}
      {(searchQuery.trim() ? crops.filter(c => (c?.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase())) : crops).map((crop, index) => (
        <Paper
          key={crop?.cropId || index}
          elevation={0}
          sx={{
            mb: { xs: 1, sm: 2 },
            p: { xs: 1, sm: 1 },
            borderRadius: 3,
            display: { xs: "block", sm: "grid" },
            gridTemplateColumns: { sm: "3fr 1fr 1fr" },
            alignItems: "center",
            border: "1px solid #E5E7EB",
            gap: { xs: 1, sm: 0 },
            transition: 'border 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              border: '2px solid #2563EB',
              boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
            },
          }}
        >
          {/* Mobile layout */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Box mb={1} display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'nowrap' }}>
              <Typography fontSize={11} color="#000" minWidth={80}>Crop Name:</Typography>

              <Box sx={{ minWidth: 0 }}>
                <Typography  fontSize={10}>{crop.name}</Typography>
              </Box>
              <Chip
                label={(crop.status || '').toUpperCase() === 'ACTIVE' ? 'Active' : (crop.status || '').toUpperCase() === 'INACTIVE' ? 'Inactive' : crop.status}
                size="small"
                sx={{
                  ml: 'auto',
                  height: 20,
                  fontSize: 11,
                  px: 0.5,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  ...((crop.status || '').toUpperCase() === 'ACTIVE'
                    ? { backgroundColor: '#D1FAE5', color: '#047857' }
                    : (crop.status || '').toUpperCase() === 'INACTIVE'
                      ? { backgroundColor: '#FEE2E2', color: '#B91C1C' }
                      : {}),
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
              <Typography fontSize={11} color="text.secondary" minWidth={80}></Typography>
              <Box>
                <IconButton size="small">
                  <EditOutlined fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>

              </Box>
            </Box>
          </Box>
          {/* Desktop layout - icons removed, show name only */}
          <Box display={{ xs: "none", sm: "flex" }} alignItems="center">
            <Typography  fontSize={{ sm: 16 }}>{crop.name}</Typography>
          </Box>
          <Chip
            sx={{
              display: { xs: "none", sm: "inline-flex" }, width: { sm: 80 }, ...(crop.status === 'Active'
                ? { backgroundColor: '#D1FAE5', color: '#047857' }
                : crop.status === 'Inactive'
                  ? { backgroundColor: '#FEE2E2', color: '#B91C1C' }
                  : {}), ...((crop.status || '').toUpperCase() === 'ACTIVE'
                    ? { backgroundColor: '#D1FAE5', color: '#047857' }
                    : (crop.status || '').toUpperCase() === 'INACTIVE'
                      ? { backgroundColor: '#FEE2E2', color: '#B91C1C' }
                      : {}),
            }}
            label={(crop.status || '').toUpperCase() === 'ACTIVE' ? 'Active' : (crop.status || '').toUpperCase() === 'INACTIVE' ? 'Inactive' : crop.status}
            size="small"
          />
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
            <IconButton size="small" onClick={() => handleEdit(crop)}>
              <EditOutlined fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
            </IconButton >

          </Box>
        </Paper>
      ))}
    </Box>
  );
}

